import {z} from 'zod';

import {isNil} from 'ramda';

import i18n from '@dms/i18n';
import {ArticleState} from '@dms/shared-aftersales';

export const ReceiveNoteItemDetailSchema = (articleState: ArticleState | null) => {
  const doesNotExist = articleState === 'doesNotExist';

  // A separate object, because a type error in any other field aborts the parent before its superRefine runs
  const unitSellingPriceSchema = z
    .object({
      isUnitSellingPriceEnabled: z.boolean(),
      // Seeded from the article even when disabled, and a new article's price is 0
      unitSellingPrice: z.number().nullish(),
    })
    .superRefine((data, ctx) => {
      if (!data.isUnitSellingPriceEnabled) {
        return;
      }

      if (isNil(data.unitSellingPrice)) {
        ctx.addIssue({
          path: ['unitSellingPrice'],
          code: z.ZodIssueCode.custom,
          message: i18n.t('errors.field_is_required'),
        });
        return;
      }

      const unitSellingPriceResult = z.number().positive().safeParse(data.unitSellingPrice);
      unitSellingPriceResult.error?.issues.forEach((issue) =>
        ctx.addIssue({...issue, path: ['unitSellingPrice']})
      );
    });

  const receiveNoteItemSchema = z.intersection(
    z.object({
      serialNumber: z.number().nullish(),
      quantity: z.number().positive(),
      unitPrice: z.number().positive(),
      totalPrice: z.number().positive(),
      vatType: z.string(),
    }),
    unitSellingPriceSchema
  );

  const deliveryItemSchema = z.object({
    serialNumber: z.number().nullish(),
    quantityVerified: z.number().positive(),
    purchasePriceVerified: z.number().positive(),
    stockValueVerified: z.number().positive(),
    vatType: z.string(),
  });

  const supplierArticleSchema = z.object({
    supplierQuantity: z.number().positive(),
    supplierUnitId: z.string().min(1),
    warehouseQuantity: z.number().positive(),
    warehouseUnitId: z.string().nullish(),
    supplierOrderingNumber: z.string().nullish(),
    supplierBulkPackageQuantity: z.number().nullish(),
    supplier: z.string().nullish(),
    supplierDiscountGroup: z.string().nullish(),
  });

  return z.object({
    article: doesNotExist
      ? z.object({
          catalogNumber: z.string(),
          manufacturerId: z.string(),
          warehouseId: z.string(),
          name: z.string(),
          storageLocationId: z.string().nullish(),
          handlingUnit: z.string(),
          dispensingUnit: z.number().nullish(),
          leafId: z.string().nullish(),
          supplierId: z.string().nullish(),
        })
      : z.record(z.any()),
    receiveNoteItem: doesNotExist ? z.record(z.any()) : receiveNoteItemSchema,
    deliveryItem: doesNotExist ? z.record(z.any()) : deliveryItemSchema,
    supplierArticle: doesNotExist ? z.record(z.any()) : supplierArticleSchema,
  });
};

export type ReceiveNoteItemFormType = z.infer<ReturnType<typeof ReceiveNoteItemDetailSchema>>;
